import { Add, EditOutlined, SearchOutlined } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	InputBase,
	Link,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
	deleteOrganization,
	listOrganizations,
	Organization,
	OrganizationStatus,
	setOrganizationEnabled,
} from "../../api/organizations";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { FacetFilter } from "../FacetFilter";
import { OrganizationDialog } from "./OrganizationDialog";
import { OrganizationMembersDialog } from "./OrganizationMembersDialog";

const headCells = ["Name", "Address", "Phone", "Website", "Members"];

export const OrganizationManagement = (): JSX.Element => {
	useDocumentTitle("IDOT – TREC Organizations");
	const [user] = useCurrentUser();
	const [organizations, setOrganizations] = useState<Organization[]>([]);
	// Site admins see and manage all organizations; other users only see their own
	const [siteAdmin, setSiteAdmin] = useState(false);
	const [membersOrgId, setMembersOrgId] = useState<number | null>(null);
	const membersOrg = organizations.find((o) => o.id === membersOrgId) ?? null;
	const [editOrgId, setEditOrgId] = useState<number | null>(null);
	const editOrg = organizations.find((o) => o.id === editOrgId) ?? null;
	// Site admins and organization admins get an Actions column (edit; enable/disable for site admins)
	const showActions = siteAdmin || organizations.some((o) => o.can_manage);
	const columns = showActions ? [...headCells, "Actions"] : headCells;
	const [searchString, setSearchString] = useState("");
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState<OrganizationStatus[]>([]);
	const [addOpen, setAddOpen] = useState(false);
	const [pendingDelete, setPendingDelete] = useState<Organization | null>(null);

	// debounce search input
	const timerRef = useRef(-1);
	useEffect(() => {
		window.clearTimeout(timerRef.current);
		timerRef.current = window.setTimeout(() => setSearch(searchString), 200);
	}, [searchString]);

	const location = useLocation();
	useEffect(() => {
		if (location.state?.searchString) {
			setSearchString(location.state.searchString);
		}
	}, [location.state]);

	const refresh = useCallback(async () => {
		try {
			const { data, metadata } = await listOrganizations({
				search,
				status: statusFilter,
			});
			setOrganizations(data);
			setSiteAdmin(metadata.site_admin);
		} catch (e) {
			console.error(e);
		}
	}, [search, statusFilter]);

	useEffect(() => {
		if (user) refresh();
	}, [user, refresh]);

	const handleToggleEnabled = async (org: Organization) => {
		try {
			await setOrganizationEnabled(org.id, org.status !== "APPROVED");
			refresh();
		} catch (e) {
			console.error(e);
		}
	};

	const handleDelete = async () => {
		if (!pendingDelete) return;
		try {
			await deleteOrganization(pendingDelete.id);
			setEditOrgId(null);
			refresh();
		} catch (e) {
			console.error(e);
		} finally {
			setPendingDelete(null);
		}
	};

	return (
		<>
			<Box
				sx={{
					boxSizing: "border-box",
					p: "60px 50px 80px",
					background: "rgba(0, 62, 98, 0.02)",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Typography fontSize={24} fontWeight={700}>
						Organizations
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						marginTop: "2%",
						justifyContent: "right",
						gap: "1%",
					}}
				>
					<Paper
						square
						elevation={0}
						sx={{
							p: "2px 4px",
							display: "flex",
							alignItems: "center",
							width: 400,
							bgcolor: "#0000000A",
						}}
					>
						<InputBase
							sx={{
								ml: 2,
								flex: 1,
								"& input::placeholder": {
									color: "#4a4a4a",
									opacity: 1,
								},
							}}
							value={searchString}
							onChange={(e) => setSearchString(e.target.value)}
							placeholder="Search by name, address or website"
						/>
						<SearchOutlined />
					</Paper>
					{siteAdmin && (
						<Button variant="contained" onClick={() => setAddOpen(true)}>
							<Add sx={{ mr: "8px" }} />
							Add New
						</Button>
					)}
				</Box>

				<Stack
					direction="row"
					gap="12px"
					flexWrap="wrap"
					alignItems="center"
					sx={{ mt: "40px", mb: "30px" }}
				>
					{siteAdmin && (
						<Box sx={{ flex: "0 1 auto" }}>
							<FacetFilter
								label="Organizations"
								value={statusFilter}
								options={[
									{ label: "Enabled", value: "APPROVED" },
									{ label: "Disabled", value: "PENDING" },
								]}
								onChange={(value) => setStatusFilter(value)}
							/>
						</Box>
					)}
				</Stack>
			</Box>
			<Box sx={{ mx: "50px", mt: "-58px", mb: "50px" }}>
				<TableContainer>
					<Table sx={{ minWidth: 750 }}>
						<TableHead>
							<TableRow>
								{columns.map((label) => (
									<TableCell key={label}>{label}</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{organizations.map((org) => (
								<TableRow key={org.id}>
									<TableCell>
										{org.name}
										{org.status === "PENDING" && org.requested_by && (
											<Typography fontSize={12} color="gray">
												Suggested by {org.requested_by}
											</Typography>
										)}
									</TableCell>
									<TableCell sx={{ whiteSpace: "pre-line" }}>
										{org.address}
									</TableCell>
									<TableCell>{org.phone}</TableCell>
									<TableCell>
										{org.url && (
											<Link href={org.url} target="_blank" rel="noopener">
												{org.url}
											</Link>
										)}
									</TableCell>
									<TableCell>
										<Button
											variant="contained"
											color="primary"
											sx={{ textTransform: "none", whiteSpace: "nowrap" }}
											onClick={() => setMembersOrgId(org.id)}
										>
											{org.members.length}{" "}
											{org.members.length === 1 ? "Member" : "Members"}
										</Button>
									</TableCell>
									{showActions && (
										<TableCell>
											<Stack direction="row" gap="8px" alignItems="center">
												{org.can_manage && (
													<Tooltip title="Edit organization">
														<IconButton onClick={() => setEditOrgId(org.id)}>
															<EditOutlined />
														</IconButton>
													</Tooltip>
												)}
												{siteAdmin && (
													<Button
														variant="contained"
														color={
															org.status === "APPROVED" ? "error" : "success"
														}
														onClick={() => handleToggleEnabled(org)}
													>
														{org.status === "APPROVED" ? "Disable" : "Enable"}
													</Button>
												)}
											</Stack>
										</TableCell>
									)}
								</TableRow>
							))}
							{organizations.length === 0 && (
								<TableRow>
									<TableCell colSpan={columns.length}>
										<Typography color="gray" align="center">
											No organizations found.
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>

			<OrganizationMembersDialog
				organization={membersOrg}
				onClose={() => setMembersOrgId(null)}
				onChanged={(updated) =>
					setOrganizations((orgs) =>
						orgs.map((o) => (o.id === updated.id ? updated : o)),
					)
				}
			/>

			<OrganizationDialog
				open={addOpen}
				onClose={() => setAddOpen(false)}
				onSaved={() => refresh()}
			/>

			<OrganizationDialog
				open={editOrg !== null}
				organization={editOrg}
				onClose={() => setEditOrgId(null)}
				onSaved={() => refresh()}
				// only site admins can delete; the confirmation dialog below opens on top
				onDelete={siteAdmin ? setPendingDelete : undefined}
			/>

			<Dialog
				open={pendingDelete !== null}
				onClose={() => setPendingDelete(null)}
			>
				<DialogTitle>Delete organization?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{pendingDelete?.name} will be removed
						{pendingDelete && pendingDelete.members.length > 0
							? ` and its ${pendingDelete.members.length} member(s) will no longer belong to an organization`
							: ""}
						. This cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPendingDelete(null)}>Cancel</Button>
					<Button color="error" variant="contained" onClick={handleDelete}>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
